// src/redux/wiresSlice.js
import { createSlice, createSelector } from "@reduxjs/toolkit";
import { nanoid } from "nanoid";

const initialState = {
  byId: {},        // { wireId: { id, type, vertices, layer, color, ... } }
  allIds: []       // Tüm wire ID listesi
};

const wiresSlice = createSlice({
  name: "wires",
  initialState,
  reducers: {
    // ✅ Wire ekle
    addWire: {
      reducer: (state, action) => {
        const { id, type, vertices, layer, color, userData, sceneUUID } = action.payload;
        state.byId[id] = { id, type, vertices, layer, color, userData, sceneUUID };
        state.allIds.push(id);
      },
      prepare: ({ id, type = "line", vertices = [], layer = "0", color = "#ffffff", userData = {}, sceneUUID }) => {
        const wireId = id || nanoid(); // id verilmezse otomatik oluştur
        return { payload: { id: wireId, type, vertices, layer, color, userData, sceneUUID } };
      }
    },

    // ✅ Wire güncelle
    updateWire: (state, action) => {
      const { id, changes } = action.payload;
      if (state.byId[id]) {
        state.byId[id] = { ...state.byId[id], ...changes };
      }
    },

    // ✅ Wire sil
    deleteWire: (state, action) => {
      const id = action.payload;
      delete state.byId[id];
      state.allIds = state.allIds.filter(wireId => wireId !== id);
    },

    // ✅ Tüm wireları temizle
    clearWires: (state) => {
      state.byId = {};
      state.allIds = [];
    }
  }
});

export const { addWire, updateWire, deleteWire, clearWires } = wiresSlice.actions;
export default wiresSlice.reducer;

// ✅ Selectorlar
const selectWiresByIdObject = (state) => state.wires.byId;
const selectWireAllIds = (state) => state.wires.allIds;

export const selectAllWires = createSelector(
  [selectWireAllIds, selectWiresByIdObject],
  (allIds, byId) => allIds.map(id => byId[id])
);

export const selectWireById = (state, id) => state.wires.byId[id];

// Eski API ile uyumluluk için
export const selectWiresAll = (state) => selectAllWires(state);

// ✅ Kablo bacası bağlantılı wire'ları getir
export const selectWiresWithCableShaftConnection = createSelector(
  [selectAllWires],
  (wires) => wires.filter(wire =>
    wire.userData?.cableShaftConnection?.startShaft ||
    wire.userData?.cableShaftConnection?.endShaft
  )
);

// ✅ Belirli bir kablo bacası grubuna bağlı wire'ları getir
export const selectWiresByShaftGroupId = (shaftGroupId) => createSelector(
  [selectAllWires],
  (wires) => wires.filter(wire => {
    const conn = wire.userData?.cableShaftConnection;
    return conn?.startShaft?.shaftGroupId === shaftGroupId ||
           conn?.endShaft?.shaftGroupId === shaftGroupId;
  })
);

// ✅ Belirli bir kat planından kablo bacasına giren wire'ları getir
export const selectWiresEnteringShaftFromFloor = (shaftGroupId, floorPlanId) => createSelector(
  [selectAllWires],
  (wires) => wires.filter(wire => {
    const conn = wire.userData?.cableShaftConnection;
    // Bu kattan kablo bacasına giren (endShaft olarak biten) wire'lar
    if (conn?.endShaft?.shaftGroupId === shaftGroupId &&
        wire.userData?.floorPlanId === floorPlanId) {
      return true;
    }
    // Bu kattan kablo bacasından başlayan (startShaft olarak başlayan) wire'lar
    if (conn?.startShaft?.shaftGroupId === shaftGroupId &&
        wire.userData?.floorPlanId === floorPlanId) {
      return true;
    }
    return false;
  })
);

// ✅ Kablo bacası grubundaki tüm katlardan gelen wire'ları getir
export const selectAllWiresInShaftGroup = (shaftGroupId) => createSelector(
  [selectAllWires],
  (wires) => {
    const result = [];
    wires.forEach(wire => {
      const conn = wire.userData?.cableShaftConnection;
      if (conn?.startShaft?.shaftGroupId === shaftGroupId) {
        result.push({
          wire,
          connectionType: 'start',
          shaftInfo: conn.startShaft,
          sourceFloorPlanId: wire.userData?.floorPlanId,
          sourceFloorPlanName: wire.userData?.floorPlanName
        });
      }
      if (conn?.endShaft?.shaftGroupId === shaftGroupId) {
        result.push({
          wire,
          connectionType: 'end',
          shaftInfo: conn.endShaft,
          sourceFloorPlanId: wire.userData?.floorPlanId,
          sourceFloorPlanName: wire.userData?.floorPlanName
        });
      }
    });
    return result;
  }
);
